import React, {useEffect, useRef, useState} from "react";
import {MedicationV3, MedicationVariation, OptionDimensionValue} from "../../types";
import {useDispatch} from "react-redux";
import {setCurrentMedicationPrice} from "../../../../../state/ducks/medication-widget/slice";
import {
    extractMedicationVariationAvailableOptionIndexes,
    findCurrentSelectionPrices,
    findLeastStrengthDrugId,
    removeWordSupplyInStringIfAny
} from "../../../utils/med-widget-utils";

export interface MedicationOptionsCardProps {
    medication: MedicationV3;
}

const generateDropdownItemsForStrength = (drugIDToVariations: Record<string, MedicationVariation>) => {
    const dropdownItems: JSX.Element[] = [];
    Object.keys(drugIDToVariations).forEach(key => dropdownItems.push(
        <pui-dropdown-item-two type="option" key={key} label={drugIDToVariations[key].strength} value={key}></pui-dropdown-item-two>));
    return dropdownItems;
}

const generateDropdownItemsForOptions = (optionDimensionValues: OptionDimensionValue[], dimensionIndexes: Set<number>) => {
    const dropdownItems: JSX.Element[] = [];
    optionDimensionValues.forEach((value, index) => {
        if (dimensionIndexes.has(index)) {
            dropdownItems.push(<pui-dropdown-item-two type="option" key={value.displayString} label={value.displayString}
                                                      value={index}></pui-dropdown-item-two>);
        }
    })
    return dropdownItems;
}

const MedicationCardOptions = ({medication}: MedicationOptionsCardProps) => {
    const drugIDToVariations = medication.drugIDToVariations;
    const dispatch = useDispatch();
    const drugOptionRef = useRef();
    const quantityOptionRef = useRef();
    const supplyOptionRef = useRef();

    //represent 3 dropdowns we have
    const [selectedDrugId, setSelectedDrugId] = useState<string>(
        //Lazy load so the function only executes when initially mounted.
        () => findLeastStrengthDrugId(drugIDToVariations)
    );
    const [selectedQuantityIndex, setSelectedQuantityIndex] = useState<number>(
        () => drugIDToVariations[selectedDrugId].options[0].dimensionIndex[0]
    );
    const [selectedSupplyIndex, setSelectedSupplyIndex] = useState<number>(
        () => drugIDToVariations[selectedDrugId].options[0].dimensionIndex[1]
    );
    //Save selectedDrugId state as a Ref so that it can be correctly retrieved in event handler
    const drugIdRef = useRef('');
    drugIdRef.current = selectedDrugId;

    useEffect(() => {
        // use an any as we don't have typings for PUI elements :(
        const drugEl: any = drugOptionRef.current;
        if (drugEl) {
            drugEl.onSelect = (newValue: string) => {
                //If newly selected drugID is different, then reset quantity and supply.
                if (newValue !== drugIdRef.current) {
                    setSelectedQuantityIndex(drugIDToVariations[newValue].options[0].dimensionIndex[0]);
                    setSelectedSupplyIndex(drugIDToVariations[newValue].options[0].dimensionIndex[1]);
                }
                setSelectedDrugId(newValue);
            }
        }

        const quantityEl: any = quantityOptionRef.current;
        if (quantityEl) {
            quantityEl.onSelect = (newValue: number) => {
                setSelectedQuantityIndex(newValue);
            }
        }

        const supplyEl: any = supplyOptionRef.current;
        if (supplyEl) {
            supplyEl.onSelect = (newValue: number) => {
                setSelectedSupplyIndex(newValue);
            }
        }
    }, [selectedDrugId]);

    useEffect(() => {
        const medicationPricing =
            findCurrentSelectionPrices(medication, selectedDrugId, selectedQuantityIndex, selectedSupplyIndex);
        !!medicationPricing && dispatch(setCurrentMedicationPrice(medicationPricing));
    }, [medication, selectedDrugId, selectedQuantityIndex, selectedSupplyIndex])

    return (
        // We need to give unique key based on drugId to each dropdown component due to PUI today has bugs on its dropdown related elements:
        //      1. <pui-dropdown-two> doesn't properly change properties when child dropdown item element list change.
        //      2. <pui-dropdown-item-two> has unfinished 'observedAttributes and 'attributeChangedCallback' implementation.
        // so we need to let React mount a new one whenever drugID changes.
        <>
            <pui-dropdown-two class="medication-options-dropdown" name="drugId"
                              label="STRENGTH" disableShowOptionsIfSingle
                              selectedText={drugIDToVariations[selectedDrugId].strength} value={selectedDrugId}
                              key={`${selectedDrugId}-strength`}
                              ref={drugOptionRef}>
                {generateDropdownItemsForStrength(drugIDToVariations)}
            </pui-dropdown-two>
            <pui-dropdown-two class="medication-options-dropdown" name="quantityPer30Days"
                              label={medication.dimensionKeys[0].displayString.toUpperCase()} disableShowOptionsIfSingle
                              selectedText={medication.dimensionValues[0][selectedQuantityIndex].displayString} value={selectedQuantityIndex}
                              key={`${selectedDrugId}-quantity`}
                              ref={quantityOptionRef}>
                {generateDropdownItemsForOptions(medication.dimensionValues[0],
                    extractMedicationVariationAvailableOptionIndexes(drugIDToVariations[selectedDrugId])[0])}
            </pui-dropdown-two>
            <pui-dropdown-two class="medication-options-dropdown" name="supply"
                              label={medication.dimensionKeys[1].displayString.toUpperCase()} disableShowOptionsIfSingle
                              selectedText={removeWordSupplyInStringIfAny(medication.dimensionValues[1][selectedSupplyIndex].displayString)}
                              value={selectedSupplyIndex}
                              key={`${selectedDrugId}-supply`}
                              ref={supplyOptionRef}>
                {generateDropdownItemsForOptions(medication.dimensionValues[1],
                    extractMedicationVariationAvailableOptionIndexes(drugIDToVariations[selectedDrugId])[1])}
            </pui-dropdown-two>
        </>
    )
}

export default MedicationCardOptions;
